const { capitalize } = require('utils/String');
const { HeadscriptRuntime, Immediate, EventType } = require('constants/events');
const {
    OPEN_SPA_PAGE_API_LOADED,
    OPEN_SPA_PAGE_DOM_UPDATED,
    OPEN_SPA_PAGE_IMAGES_PRELOADED,
    OPEN_SPA_PAGE_IMAGES_PRELOADING,
    OPEN_SPA_PAGE_START
} = require('constants/performance/marks');
const {
    SEPHORA_URL: { DESKTOP }
} = require('components/constants');
const { SpaTemplatesInfo } = require('constants/SpaTemplatesInfo');
const bccUtil = require('utils/BCC');
const getStoreLocations = require('services/api/utility/storeLocator/getStoreLocations');
const LOCAL_STORAGE = require('utils/localStorage/Constants');
const LocationUtils = require('utils/Location');
const PageTemplateType = require('constants/PageTemplateType');
const Storage = require('utils/localStorage/Storage');
const store = require('Store');
const storeUtils = require('utils/Store');
const UrlUtils = require('utils/Url');
const UserActions = require('actions/UserActions');
const localeUtils = require('utils/LanguageLocale');
const { seo } = require('constants/seo');

const LocalePathPrefix = /(?:\/ca\/[e | f][n | r])*/;

const SpaUtils = {
    replaceContentAreaTargeters: function (currentData = {}, newData = {}) {
        let allPageTargeterNames = Sephora.targetersToInclude.substr('?includeTargeters='.length);
        allPageTargeterNames = decodeURIComponent(allPageTargeterNames).split(',');

        // remove old content area targeters
        const currentTargeterNames = bccUtil.extractTargeters(currentData);

        for (const targeterName of currentTargeterNames) {
            const index = allPageTargeterNames.indexOf(targeterName);

            if (index !== -1) {
                allPageTargeterNames.splice(index, 1);
            }
        }

        // add new content area targeters
        const newTargeterNames = bccUtil.extractTargeters(newData);
        allPageTargeterNames = allPageTargeterNames.concat(newTargeterNames);
        Sephora.targetersToInclude = bccUtil.buildTargetersQueryParams(allPageTargeterNames);
    },

    updateUserFull: function (data, productPageData = {}, options = {}) {
        const currentUser = Sephora.Util.getCurrentUser();
        const basketCache = Sephora.Util.getBasketCache(currentUser);
        const userInfoCache = Sephora.Util.getUserInfoCache();

        let shouldGetUserFull = false;
        let userFullOptions = undefined;

        let apiToInclude;

        if (Sephora.Util.shouldGetTargeters() && Sephora.Util.shouldGetTargetedPromotion()) {
            apiToInclude = ['targetedPromotion'];
            userFullOptions = { includeApis: apiToInclude };
            shouldGetUserFull = true;
        }

        if (Sephora.Util.shouldGetUserFull(basketCache, currentUser, userInfoCache)) {
            shouldGetUserFull = true;

            if (!userFullOptions) {
                userFullOptions = { includeApis: [] };
            }

            userFullOptions.includeApis.push('profile', 'basket', 'loves', 'shoppingList', 'segments');
        }

        if (shouldGetUserFull) {
            store.dispatch(
                UserActions.getUserFull(
                    productPageData,
                    null,
                    userFullOptions
                        ? {
                            ...userFullOptions,
                            includeApis: userFullOptions.includeApis.join(',')
                        }
                        : undefined,
                    undefined,
                    options.hideLoader || false
                )
            );
        }
    },

    updatePreferredStoreDetails: function (user) {
        const cachedStoreData = Storage.session.getItem(LOCAL_STORAGE.SELECTED_STORE);

        if (user?.preferredStore && !cachedStoreData) {
            getStoreLocations(user.preferredStore).then(data => {
                const preferredStoreInfo = data.stores[0];
                storeUtils.cacheStoreData(preferredStoreInfo);
                store.dispatch(UserActions.updatePreferredStore({ preferredStoreInfo }));
            });
        }
    },

    resetCertona: function () {
        require('services/Certona')();
    },

    resetDigitalData: function () {
        digitalData.lastRefresh = 'soft';
        digitalData.page.attributes.featureVariantKeys = [];
        digitalData.page.attributes.personalizedEmailCampaign = '';
        digitalData.page.attributes.previousPageData.recInfo = {};
        delete digitalData.page.renderTime;
        delete digitalData.page.SpaApiCallTime;
        delete digitalData.performance.renderTime;
        delete digitalData.performance.spaAPITime;
        delete digitalData.performance.spaDOMUpdateTime;
        delete digitalData.performance.spaDOMUpdateWithoutAPITime;
        delete digitalData.performance.spaPreloadImagesTime;

        digitalData.product = [];
    },

    updateDigitalData: (eventName, eventTime) => {
        switch (eventName) {
            case `${HeadscriptRuntime} ${EventType.Applied}`: {
                const measure = performance.measure(
                    HeadscriptRuntime,
                    `${HeadscriptRuntime} ${EventType.Loaded}`,
                    `${HeadscriptRuntime} ${EventType.Applied}`
                );
                let duration;

                // In Firefox performance.measure() does not return PerformanceMeasure entry according MDN documentation.
                // See https://developer.mozilla.org/en-US/docs/Web/API/Performance/measure#return_value for more info.
                // It's Firefox issue!
                // Code in else branch is workaround for this scenario.
                if (measure) {
                    duration = measure.duration;
                } else {
                    const measureEntries = performance.getEntriesByName(HeadscriptRuntime, 'measure');
                    const lastMeasure = measureEntries[measureEntries.length - 1];
                    duration = lastMeasure.duration;
                }

                digitalData.performance.headScriptRuntime = duration;

                break;
            }
            case `${Immediate} ${EventType.Ready}`: {
                digitalData.performance.applicationStart = eventTime;

                break;
            }
            case 'HydrationFinished': {
                digitalData.performance.hydrationFinished = eventTime;

                break;
            }
            case 'Post Load Rendered': {
                digitalData.performance.postLoadRendered = eventTime;

                break;
            }
            case 'InPageComps Service Ctrlrs Applied': {
                digitalData.performance.inPageCompsApplied = eventTime;

                break;
            }
            case 'Certona Service Ctrlrs Applied': {
                const loadedEvent = Sephora.performance.renderTime.getFirstEventOrDefault('Certona Loaded');
                const appliedEvent = Sephora.performance.renderTime.getFirstEventOrDefault(eventName);
                digitalData.performance.certonaLoaded = loadedEvent.startTime;
                digitalData.performance.certonaApplied = appliedEvent.startTime;

                break;
            }
            case 'UserInfo Service Ctrlrs Applied': {
                const loadedEvent = Sephora.performance.renderTime.getFirstEventOrDefault('UserInfo Loaded');
                const appliedEvent = Sephora.performance.renderTime.getFirstEventOrDefault(eventName);
                digitalData.performance.userInfoLoaded = loadedEvent.startTime;
                digitalData.performance.userInfoApplied = appliedEvent.startTime;

                break;
            }
            case 'TestTarget Service Ctrlrs Applied': {
                const loadedEvent = Sephora.performance.renderTime.getFirstEventOrDefault('TestTarget Loaded');
                const appliedEvent = Sephora.performance.renderTime.getFirstEventOrDefault(eventName);
                digitalData.performance.testTargetLoaded = loadedEvent.startTime;
                digitalData.performance.testTargetApplied = appliedEvent.startTime;

                break;
            }
            case 'Page Rendered': {
                Sephora.Util.InflatorComps.totalReactTimeOnPageRender = Sephora.Util.InflatorComps.totalRenderTime;
                const startEvent = Sephora.performance.renderTime.getFirstEventOrDefault(OPEN_SPA_PAGE_START);
                digitalData.performance.renderTime = eventTime - startEvent.startTime;
                // Delete the line below after stop using mPulse
                // Also it's used in Signal => Async :: Page Load Time
                // https://hub.signal.co/sites/szZFBFc/data/1568015/inputs
                digitalData.page.renderTime = digitalData.performance.renderTime;

                break;
            }
            case OPEN_SPA_PAGE_API_LOADED: {
                const startEvent = Sephora.performance.renderTime.getFirstEventOrDefault(OPEN_SPA_PAGE_START);
                digitalData.performance.spaAPITime = eventTime - startEvent.startTime;
                // Delete the line below after stop using mPulse
                digitalData.page.SpaApiCallTime = digitalData.performance.spaAPITime;

                break;
            }
            case OPEN_SPA_PAGE_IMAGES_PRELOADED: {
                const startEvent = Sephora.performance.renderTime.getFirstEventOrDefault(OPEN_SPA_PAGE_IMAGES_PRELOADING);
                digitalData.performance.spaPreloadImagesTime = eventTime - startEvent.startTime;

                break;
            }
            case OPEN_SPA_PAGE_DOM_UPDATED: {
                const startEvent = Sephora.performance.renderTime.getFirstEventOrDefault(OPEN_SPA_PAGE_START);
                const apiEvent = Sephora.performance.renderTime.getFirstEventOrDefault(OPEN_SPA_PAGE_API_LOADED);
                digitalData.performance.spaDOMUpdateTime = eventTime - startEvent.startTime;
                digitalData.performance.spaDOMUpdateWithoutAPITime = eventTime - apiEvent.startTime;

                break;
            }
            default: {
                // Do nothing.
            }
        }
    },

    resetTestAndTarget: (testTarget, notPreBasket = false) => {
        const { TestTargetServiceReady } = require('utils/framework/Events');
        const {
            OFFERS_READY_STATES: { ADOBE_TESTS_RECEIVED }
        } = require('actions/TestTargetActions');
        const testTargetService = require('services/TestTarget');

        const doNotResetTestAndTarget = testTarget?.readyState === ADOBE_TESTS_RECEIVED;
        const options = { doNotResetTestAndTarget };

        if (notPreBasket) {
            options.notPreBasket = true;
        }

        if (!doNotResetTestAndTarget) {
            Sephora.Util.InflatorComps.services.loadEvents[TestTargetServiceReady] = false;
        }

        testTargetService.initialize(options);
    },

    resetAddStructure: function () {
        require('services/AddStructure')();
    },

    updateHeaderTags: (data, nextPageTemplate) => {
        const template = store.getState().page?.templateInformation?.template;
        const templateToCheck = nextPageTemplate || template;
        const isBuyPage = templateToCheck === PageTemplateType.RwdBuyPage;
        const isProductPage = templateToCheck === PageTemplateType.ProductPage;
        const seoParams = isProductPage ? data?.content : isBuyPage ? data?.rawBuyPageInfo : data;
        const isSalePage = LocationUtils.isSalePage();
        const isHomepage = LocationUtils.isHomepage();

        if (!seoParams) {
            return;
        }

        let { seoCanonicalUrl, seoMetaDescription, seoTitle } = seoParams;

        if (seoParams.seo) {
            const { pageTitle, metaDescription, canonicalUrl } = seoParams.seo;
            seoTitle = pageTitle;
            seoMetaDescription = metaDescription;
            seoCanonicalUrl = canonicalUrl;
        }

        if (isBuyPage) {
            const customSeoTitle = capitalize(data?.rawBuyPageInfo?.slug || '');
            seoTitle = `${customSeoTitle} | Sephora`;
            seoMetaDescription = `${customSeoTitle} are available now at Sephora! Shop ${customSeoTitle} and find the best fit for your beauty routine. Free shipping and samples available.`;
            seoCanonicalUrl = `/buy/${customSeoTitle}`;
        }

        if (isSalePage) {
            const versions = seo.find(x => x.url === '/sale').localeVersions;
            const localizedSaleSEO =
                versions.find(x => x.locale === localeUtils.getCurrentLanguageCountryCode()) || versions.find(x => x.locale === 'en_US');
            seoTitle = localizedSaleSEO.seoTitle;
            seoMetaDescription = localizedSaleSEO.seoMetaDescription;
            seoCanonicalUrl = localizedSaleSEO.seoMetaDescription;
        }

        document.title = SpaUtils.getPageTitle(seoTitle, template);

        if (seoTitle) {
            document.getElementById('metaOgTitle')?.setAttribute('content', seoTitle);
        }

        if (seoMetaDescription) {
            document.getElementById('metaDescription')?.setAttribute('content', seoMetaDescription);
            document.getElementById('metaOgDescription')?.setAttribute('content', seoMetaDescription);
        }

        if (seoCanonicalUrl) {
            const link = `${seoCanonicalUrl && DESKTOP}${isHomepage ? '/' : seoCanonicalUrl}`;
            const url = UrlUtils.getLink(link);
            document.getElementById('seoCanonicalUrl')?.setAttribute('href', url);
            document.getElementById('metaOgUrl')?.setAttribute('content', url);
        }
    },

    getPageTitle: (seoTitle, template) => {
        let title = 'Sephora';

        if (seoTitle) {
            title = seoTitle;
        } else if (LocationUtils.getPageTitle(template)) {
            const postfix = template.indexOf('Community') === -1 ? ' | Sephora' : '';
            title = `${LocationUtils.getPageTitle(template)}${postfix}`;
        }

        return title;
    },

    isSpaNavigation: ({ hostName, path }) =>
        hostName === window.location.hostname &&
        Sephora.isSPA &&
        Sephora.configurationSettings.spaEnabled &&
        SpaUtils.currentPageIsActiveSpaPage() &&
        SpaUtils.nextPageIsActiveSpaPage(path),

    currentPageIsActiveSpaPage: () => {
        const {
            page: {
                templateInformation: { template }
            }
        } = store.getState();
        const templateInfo = SpaUtils.getSpaTemplateInfoByTemplate(template);

        return SpaUtils.getKillSwitchValueOrDefault(templateInfo, false);
    },

    nextPageIsActiveSpaPage: nextPageUrl => {
        const templateInfo = SpaUtils.getSpaTemplateInfoByUrl(nextPageUrl);

        return SpaUtils.getKillSwitchValueOrDefault(templateInfo, false);
    },

    getKillSwitchValueOrDefault: (templateInfo, defaultValue) => {
        const killSwitchName = templateInfo?.killSwitchName;
        const result =
            killSwitchName && Object.prototype.hasOwnProperty.call(Sephora.configurationSettings, killSwitchName)
                ? Sephora.configurationSettings[killSwitchName]
                : killSwitchName === undefined
                    ? false
                    : defaultValue;

        return result;
    },

    getSpaTemplateInfoByTemplate: template => SpaTemplatesInfo.find(x => x.template === template),

    normalizePath: path => path.replace(LocalePathPrefix, ''),

    getSpaTemplateInfoByUrl: pageUrl => {
        const normalizedPath = SpaUtils.normalizePath(pageUrl);

        return SpaTemplatesInfo.find(info => {
            if (info.url instanceof RegExp) {
                return info.url.test(normalizedPath);
            }

            return normalizedPath.startsWith(info.url);
        });
    },

    isSpaTemplate: template => SpaTemplatesInfo.some(x => x.template === template)
};

module.exports = SpaUtils;
